import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoginTransition from '../../components/feature/LoginTransition';
import SEO from '../../components/feature/SEO';

type UserRole = 'admin' | 'user';
type Mode = 'login' | 'register' | 'forgot' | 'reset' | 'admin';

interface Account {
  email: string;
  password: string;
  role: UserRole;
  name: string;
  company?: string;
  phone?: string;
}

const DEFAULT_ACCOUNTS: Account[] = [
  { email: 'admin@nsgroupltd.com', password: 'Nasirmirza1989', role: 'admin', name: '管理者' },
  { email: 'user@nsgroupltd.com', password: 'nsgroup2024user', role: 'user', name: 'ユーザー' },
];

function getAccounts(): Account[] {
  try {
    const stored = localStorage.getItem('accounts');
    if (stored) {
      const parsed: Account[] = JSON.parse(stored);
      const merged = [...DEFAULT_ACCOUNTS];
      parsed.forEach((a) => {
        if (!merged.find((m) => m.email === a.email)) merged.push(a);
      });
      return merged;
    }
  } catch { /* ignore */ }
  return DEFAULT_ACCOUNTS;
}

function saveAccount(account: Account) {
  try {
    const stored = localStorage.getItem('accounts');
    const existing: Account[] = stored ? JSON.parse(stored) : [];
    existing.push(account);
    localStorage.setItem('accounts', JSON.stringify(existing));
  } catch { /* ignore */ }
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<Mode>('login');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // トランジション用
  const [showTransition, setShowTransition] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState('/');
  const [transitionUser, setTransitionUser] = useState('');

  const [regName, setRegName] = useState('');
  const [regCompany, setRegCompany] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);

  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    await new Promise((res) => setTimeout(res, 600));
    const accounts = getAccounts();
    const matched = accounts.find((a) => a.email === loginEmail && a.password === loginPassword);
    if (matched) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userEmail', matched.email);
      localStorage.setItem('userRole', matched.role);
      localStorage.setItem('userName', matched.name);
      setTransitionUser(matched.company || matched.name);
      setTransitionTarget('/');
      setShowTransition(true);
    } else {
      setLoginError(t('login.error'));
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (!regName.trim()) { setRegError(t('login.reg_error_name')); return; }
    if (regPassword.length < 6) { setRegError(t('login.reg_error_short')); return; }
    if (regPassword !== regPasswordConfirm) { setRegError(t('login.reg_error_mismatch')); return; }
    const accounts = getAccounts();
    if (accounts.find((a) => a.email.toLowerCase() === regEmail.toLowerCase())) { setRegError(t('login.reg_error_exists')); return; }
    setRegLoading(true);
    await new Promise((res) => setTimeout(res, 700));
    saveAccount({ email: regEmail, password: regPassword, role: 'user', name: regName, company: regCompany, phone: regPhone });
    setRegLoading(false);
    setRegSuccess(true);
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    await new Promise((res) => setTimeout(res, 700));
    const accounts = getAccounts();
    const matched = accounts.find((a) => a.email === forgotEmail);
    if (!matched) { setForgotError(t('login.forgot_error')); setForgotLoading(false); return; }
    setForgotLoading(false);
    setResetEmail(forgotEmail);
    setResetPassword('');
    setResetPasswordConfirm('');
    setResetError('');
    setResetSuccess(false);
    setMode('reset');
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    setResetError('');
    if (resetPassword.length < 6) { setResetError(t('login.reg_error_short')); return; }
    if (resetPassword !== resetPasswordConfirm) { setResetError(t('login.reg_error_mismatch')); return; }
    setResetLoading(true);
    await new Promise((res) => setTimeout(res, 700));
    try {
      const stored = localStorage.getItem('accounts');
      const existing: Account[] = stored ? JSON.parse(stored) : [];
      const idx = existing.findIndex((a) => a.email === resetEmail);
      if (idx >= 0) {
        existing[idx].password = resetPassword;
      } else {
        const def = DEFAULT_ACCOUNTS.find((a) => a.email === resetEmail);
        if (def) existing.push({ ...def, password: resetPassword });
      }
      localStorage.setItem('accounts', JSON.stringify(existing));
    } catch { /* ignore */ }
    setResetLoading(false);
    setResetSuccess(true);
  };

  const handleAdminLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    await new Promise((res) => setTimeout(res, 600));
    const accounts = getAccounts();
    const matched = accounts.find(
      (a) => a.email === loginEmail && a.password === loginPassword && a.role === 'admin'
    );
    if (matched) {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userEmail', matched.email);
      localStorage.setItem('userRole', matched.role);
      localStorage.setItem('userName', matched.name);
      setTransitionUser(matched.company || matched.name);
      setTransitionTarget('/admin');
      setShowTransition(true);
    } else {
      setLoginError(t('login.admin_error'));
      setLoginLoading(false);
    }
  };

  const switchToLogin = () => {
    setMode('login');
    setRegSuccess(false);
    setRegError('');
    setRegName('');
    setRegCompany('');
    setRegPhone('');
    setRegEmail('');
    setRegPassword('');
    setRegPasswordConfirm('');
    setForgotEmail('');
    setForgotError('');
    setResetSuccess(false);
  };

  const isForgotOrReset = mode === 'forgot' || mode === 'reset';
  const currentLang = i18n.language?.startsWith('ja') ? 'ja' : 'en';

  const toggleLang = () => {
    i18n.changeLanguage(currentLang === 'ja' ? 'en' : 'ja');
  };

  return (
    <>
      <SEO
        title="ログイン | N's Group Ltd."
        description="N's Group Ltd.のログインページ"
        noindex={true}
      />
      {showTransition && (
        <LoginTransition
          userName={transitionUser}
          onComplete={() => navigate(transitionTarget)}
        />
      )}
      <div className="min-h-screen relative flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'url(https://public.readdy.ai/ai/img_res/93f42a9320b0de3bfa34ed10af6d8296.jpg)',
          }}
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70" />

        {/* Decorative blurred circles */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl pointer-events-none" />

        {/* Language toggle */}
        <button
          onClick={toggleLang}
          className="absolute top-4 right-4 sm:top-5 sm:right-6 z-20 flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-translate-2 text-sm" />
          </div>
          {currentLang === 'ja' ? 'EN' : 'JA'}
        </button>

        <div className="relative z-10 w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="relative inline-flex flex-col items-center">
              {/* Logo container */}
              <div className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl shadow-2xl shadow-black/40 mb-3 sm:mb-4 border border-white/30">
                <img
                  src="https://static.readdy.ai/image/310af9d506808f9a31b9fe6283e83b26/5a0223c22c87ad007bff1a2065ab36b4.png"
                  alt="N's Group Ltd."
                  className="h-11 sm:h-14 w-auto object-contain"
                />
              </div>
              {/* Company name with decorative lines */}
              <div className="flex items-center gap-2 sm:gap-3 mb-1">
                <div className="w-6 sm:w-8 h-px bg-gradient-to-r from-transparent to-amber-400/60" />
                <h1 className="text-lg sm:text-xl font-bold text-white tracking-[0.15em] sm:tracking-[0.2em] uppercase">
                  N&apos;s Group Ltd.
                </h1>
                <div className="w-6 sm:w-8 h-px bg-gradient-to-l from-transparent to-amber-400/60" />
              </div>
              <p className="text-xs text-white/50 tracking-widest uppercase">{t('login.portal')}</p>
            </div>
          </div>

          {/* Tab Switcher */}
          {!isForgotOrReset && (
            <div className="flex bg-white/10 backdrop-blur-sm border border-white/20 rounded-full p-1 mb-4 sm:mb-5">
              {(['login', 'register', 'admin'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setMode(tab);
                    setLoginError('');
                    setLoginEmail('');
                    setLoginPassword('');
                    setRegSuccess(false);
                    setRegError('');
                  }}
                  className={`flex-1 py-2 text-xs font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    mode === tab
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {tab === 'login' && t('login.tab_login')}
                  {tab === 'register' && t('login.tab_register')}
                  {tab === 'admin' && t('login.tab_admin')}
                </button>
              ))}
            </div>
          )}

          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-5 sm:p-8">

            {/* ===== LOGIN ===== */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.email')}</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                    placeholder={t('login.emailPlaceholder')}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-white/80">{t('login.password')}</label>
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(''); setForgotError(''); setMode('forgot'); }}
                      className="text-xs text-white/50 hover:text-white underline cursor-pointer whitespace-nowrap transition-colors"
                    >
                      {t('login.forgot_password')}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 pr-11 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                      placeholder={t('login.passwordPlaceholder')}
                    />
                    <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
                      <i className={showLoginPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>
                {loginError && <ErrorBox msg={loginError} />}
                <button type="submit" disabled={loginLoading}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {loginLoading ? <LoadingSpinner label={t('login.loggingIn')} /> : t('login.submit')}
                </button>
                <p className="text-center text-xs text-white/50 pt-1">
                  {t('login.no_account')}
                  <button type="button" onClick={() => setMode('register')}
                    className="text-white font-semibold underline ml-1 cursor-pointer">{t('login.tab_register')}</button>
                </p>
              </form>
            )}

            {/* ===== ADMIN ===== */}
            {mode === 'admin' && (
              <form onSubmit={handleAdminLogin} className="space-y-4 sm:space-y-5">
                <div className="text-center mb-2">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 border border-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-shield-user-line text-xl sm:text-2xl text-white/80" />
                  </div>
                  <h2 className="text-sm sm:text-base font-bold text-white">{t('login.admin_title')}</h2>
                  <p className="text-xs text-white/50 mt-1">{t('login.admin_subtitle')}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.email')}</label>
                  <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                    placeholder="admin@nsgroupltd.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.password')}</label>
                  <div className="relative">
                    <input type={showLoginPassword ? 'text' : 'password'} value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required
                      className="w-full px-4 py-3 pr-11 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                      placeholder={t('login.passwordPlaceholder')} />
                    <button type="button" onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
                      <i className={showLoginPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                </div>
                {loginError && <ErrorBox msg={loginError} />}
                <button type="submit" disabled={loginLoading}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {loginLoading ? <LoadingSpinner label={t('login.loggingIn')} /> : t('login.admin_submit')}
                </button>
              </form>
            )}

            {/* ===== REGISTER ===== */}
            {mode === 'register' && !regSuccess && (
              <form onSubmit={handleRegister} className="space-y-3 sm:space-y-4">
                <InputField label={`${t('login.reg_name')} *`} id="reg-name" type="text"
                  value={regName} onChange={setRegName} placeholder={t('login.reg_name_placeholder')} />
                <InputField label={`${t('login.reg_company')}`} id="reg-company" type="text"
                  value={regCompany} onChange={setRegCompany} placeholder={t('login.reg_company_placeholder')} required={false} />
                <InputField label={`${t('login.reg_phone')}`} id="reg-phone" type="tel"
                  value={regPhone} onChange={setRegPhone} placeholder={t('login.reg_phone_placeholder')} required={false} />
                <InputField label={`${t('login.reg_email')} *`} id="reg-email" type="email"
                  value={regEmail} onChange={setRegEmail} placeholder={t('login.emailPlaceholder')} />
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.reg_password')} <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <input type={showRegPassword ? 'text' : 'password'} value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required
                      className="w-full px-4 py-3 pr-11 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                      placeholder={t('login.reg_password_placeholder')} />
                    <button type="button" onClick={() => setShowRegPassword(!showRegPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
                      <i className={showRegPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  <p className="text-xs text-white/40 mt-1">{t('login.reg_password_hint')}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.reg_password_confirm')} <span className="text-red-400">*</span></label>
                  <input type={showRegPassword ? 'text' : 'password'} value={regPasswordConfirm} onChange={(e) => setRegPasswordConfirm(e.target.value)} required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                    placeholder={t('login.reg_password_confirm_placeholder')} />
                </div>
                {regError && <ErrorBox msg={regError} />}
                <button type="submit" disabled={regLoading}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {regLoading ? <LoadingSpinner label={t('login.reg_submitting')} /> : t('login.reg_submit')}
                </button>
                <p className="text-center text-xs text-white/50 pt-1">
                  {t('login.have_account')}
                  <button type="button" onClick={switchToLogin}
                    className="text-white font-semibold underline ml-1 cursor-pointer">{t('login.tab_login')}</button>
                </p>
              </form>
            )}

            {/* ===== REGISTER SUCCESS ===== */}
            {mode === 'register' && regSuccess && (
              <div className="text-center py-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-green-500/20 border border-green-400/30 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl sm:text-4xl text-green-400" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white mb-2">{t('login.reg_success_title')}</h3>
                <p className="text-sm text-white/60 mb-6 whitespace-pre-line">{t('login.reg_success_msg')}</p>
                <button onClick={switchToLogin}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap">
                  {t('login.reg_to_login')}
                </button>
              </div>
            )}

            {/* ===== FORGOT PASSWORD ===== */}
            {mode === 'forgot' && (
              <form onSubmit={handleForgot} className="space-y-4 sm:space-y-5">
                <div className="text-center mb-2">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 border border-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-lock-password-line text-xl sm:text-2xl text-white/80" />
                  </div>
                  <h2 className="text-sm sm:text-base font-bold text-white">{t('login.forgot_title')}</h2>
                  <p className="text-xs text-white/50 mt-1 whitespace-pre-line">{t('login.forgot_subtitle')}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.email')}</label>
                  <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                    placeholder={t('login.emailPlaceholder')} />
                </div>
                {forgotError && <ErrorBox msg={forgotError} />}
                <button type="submit" disabled={forgotLoading}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {forgotLoading ? <LoadingSpinner label={t('login.forgot_checking')} /> : t('login.forgot_submit')}
                </button>
                <button type="button" onClick={switchToLogin}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line text-sm" />
                  {t('login.back_to_login')}
                </button>
              </form>
            )}

            {/* ===== RESET PASSWORD ===== */}
            {mode === 'reset' && !resetSuccess && (
              <form onSubmit={handleReset} className="space-y-4 sm:space-y-5">
                <div className="text-center mb-2">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 border border-white/20 rounded-full mx-auto mb-3">
                    <i className="ri-shield-keyhole-line text-xl sm:text-2xl text-white/80" />
                  </div>
                  <h2 className="text-sm sm:text-base font-bold text-white">{t('login.reset_title')}</h2>
                  <p className="text-xs text-white/50 mt-1">
                    <span className="font-medium text-white/70">{resetEmail}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.reset_new_password')} <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <input type={showResetPassword ? 'text' : 'password'} value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} required
                      className="w-full px-4 py-3 pr-11 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                      placeholder={t('login.reg_password_placeholder')} />
                    <button type="button" onClick={() => setShowResetPassword(!showResetPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/40 hover:text-white cursor-pointer">
                      <i className={showResetPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                    </button>
                  </div>
                  <p className="text-xs text-white/40 mt-1">{t('login.reg_password_hint')}</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-white/80 mb-1.5">{t('login.reset_new_password_confirm')} <span className="text-red-400">*</span></label>
                  <input type={showResetPassword ? 'text' : 'password'} value={resetPasswordConfirm} onChange={(e) => setResetPasswordConfirm(e.target.value)} required
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
                    placeholder={t('login.reg_password_confirm_placeholder')} />
                </div>
                {resetError && <ErrorBox msg={resetError} />}
                <button type="submit" disabled={resetLoading}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                  {resetLoading ? <LoadingSpinner label={t('login.reset_updating')} /> : t('login.reset_submit')}
                </button>
                <button type="button" onClick={() => setMode('forgot')}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line text-sm" />
                  {t('login.back_to_login')}
                </button>
              </form>
            )}

            {/* ===== RESET SUCCESS ===== */}
            {mode === 'reset' && resetSuccess && (
              <div className="text-center py-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-green-500/20 border border-green-400/30 rounded-full mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-3xl sm:text-4xl text-green-400" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-white mb-2">{t('login.reset_success_title')}</h3>
                <p className="text-sm text-white/60 mb-6">{t('login.reset_success_msg')}</p>
                <button onClick={switchToLogin}
                  className="w-full bg-white text-gray-900 py-3 rounded-lg text-sm font-bold hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap">
                  {t('login.reset_to_login')}
                </button>
              </div>
            )}
          </div>

          <p className="text-center text-xs text-white/30 mt-4 sm:mt-6">{t('login.copyright')}</p>
        </div>
      </div>
    </>
  );
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3 flex items-center gap-2">
      <div className="w-4 h-4 flex items-center justify-center shrink-0">
        <i className="ri-error-warning-line text-red-400 text-sm" />
      </div>
      <p className="text-xs text-red-300">{msg}</p>
    </div>
  );
}

function LoadingSpinner({ label }: { label: string }) {
  return (
    <span className="flex items-center justify-center gap-2">
      <i className="ri-loader-4-line animate-spin" />
      {label}
    </span>
  );
}

function InputField({
  label, id, type, value, onChange, placeholder, required = true,
}: {
  label: string; id: string; type: string; value: string;
  onChange: (v: string) => void; placeholder: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-semibold text-white/80 mb-1.5">{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-transparent transition-all"
        placeholder={placeholder} />
    </div>
  );
}
